import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, EmptyState, Section, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { WebVitalsOverview } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Sayfa hızı (F5-14): PageSpeed Insights ile ana sayfa, kategori, ürün ve blog yazısının mobil/masaüstü ölçümü.
 * Saha verisi (gerçek kullanıcılar, son 28 gün) Google'ın Core Web Vitals değerlendirmesidir; laboratuvar puanı
 * tek seferlik test ortamı ölçümüdür.
 */

type Result = WebVitalsOverview['results'][number];
type Rating = 'good' | 'needs_improvement' | 'poor';
const TONE: Record<Rating, 'moss' | 'amber' | 'rose'> = {
  good: 'moss',
  needs_improvement: 'amber',
  poor: 'rose',
};
const scoreRating = (s: number): Rating => (s >= 90 ? 'good' : s >= 50 ? 'needs_improvement' : 'poor');
const KNOWN_ERRORS = [
  'psi_page_failed',
  'psi_key_invalid',
  'psi_quota',
  'psi_timeout',
  'psi_unreachable',
  'pagespeed_not_configured',
  'store_has_no_domain',
] as const;

export function WebVitalsPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['web-vitals', storeId],
    queryFn: () => api.get<WebVitalsOverview>(`/v1/admin/web-vitals?storeId=${storeId}`),
    enabled: !!storeId,
    // Ölçüm sürerken birkaç saniyede bir yenilenir (bir ölçüm birkaç dakika sürebilir).
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => r.status === 'queued' || r.status === 'running') ? 3_000 : false,
  });
  return (
    <PageFrame helpKey="page.web_vitals">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <StatusSection storeId={storeId} d={q.data} />
          {q.data.results.length > 0 && <ResultsSection d={q.data} />}
          {q.data.runs.length > 0 && <RunsSection d={q.data} />}
        </>
      )}
    </PageFrame>
  );
}

function errorText(t: ReturnType<typeof useT>, code: string | null) {
  if (!code) return '';
  return (KNOWN_ERRORS as readonly string[]).includes(code)
    ? t(`admin.webVitals.errors.${code}`)
    : t('admin.webVitals.errors.unknown');
}

function StatusSection({ storeId, d }: { storeId: string; d: WebVitalsOverview }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const busy = d.runs.some((r) => r.status === 'queued' || r.status === 'running');
  const start = useApiMutation(() => api.post('/v1/admin/web-vitals', { storeId }), {
    invalidate: [['web-vitals', storeId]],
    success: t('admin.webVitals.started'),
  });
  return (
    <Section
      title={t('admin.webVitals.statusTitle')}
      description={t('admin.webVitals.statusLead')}
      action={<HelpTip k="web_vitals.run" />}
    >
      {!d.configured ? (
        <EmptyState
          title={t('admin.webVitals.notConfigured')}
          description={t('admin.webVitals.notConfiguredLead')}
          action={
            <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
              {t('admin.nav.integrations')}
            </Link>
          }
        />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          {can('seo.write') && (
            <Button loading={start.isPending} disabled={busy} onClick={() => start.mutate(undefined)}>
              {busy ? t('admin.webVitals.running') : t('admin.webVitals.start')}
            </Button>
          )}
          <span className="text-sm text-ink-2">
            {d.latestRunAt
              ? t('admin.webVitals.lastRun', { time: formatDate(d.latestRunAt, true) })
              : t('admin.webVitals.neverRun')}
          </span>
        </div>
      )}
    </Section>
  );
}

function Metric({ value, rating }: { value: string | null; rating: Rating | null }) {
  const t = useT();
  if (value === null) return <span className="text-ink-3">—</span>;
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span className="tabular-nums">{value}</span>
      {rating && <Badge tone={TONE[rating]}>{t(`admin.webVitals.ratings.${rating}`)}</Badge>}
    </span>
  );
}

/** Saniye (bir ondalık) ve CLS (iki ondalık), dilin sayı biçimiyle. */
function useFormats() {
  const { formatNumber } = useI18n();
  return {
    sec: (ms: number | null) => (ms === null ? null : formatNumber(ms / 1000, 1)),
    cls: (milli: number | null) => (milli === null ? null : formatNumber(milli / 1000, 2)),
  };
}

function ResultsSection({ d }: { d: WebVitalsOverview }) {
  const t = useT();
  return (
    <Section
      title={t('admin.webVitals.resultsTitle')}
      description={t('admin.webVitals.resultsLead')}
      action={<HelpTip k="web_vitals.results" />}
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.webVitals.resultsTitle')}>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.webVitals.colPage')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.webVitals.colDevice')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.webVitals.colScore')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.webVitals.colLcp')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.webVitals.colInp')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.webVitals.colCls')}</th>
            </tr>
          </thead>
          <tbody>
            {d.results.map((r) => (
              <ResultRow key={`${r.template}-${r.strategy}`} r={r} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-sm text-ink-3">{t('admin.webVitals.thresholds')}</p>
    </Section>
  );
}

function ResultRow({ r }: { r: Result }) {
  const t = useT();
  const { sec, cls } = useFormats();
  const diff = r.lab.score !== null && r.previousScore !== null ? r.lab.score - r.previousScore : null;
  return (
    <tr className="border-t border-line align-top">
      <td className="min-w-64 px-3 py-2">
        <span className="font-medium">{t(`admin.webVitals.templates.${r.template}`)}</span>
        <a
          href={r.url}
          target="_blank"
          rel="noreferrer noopener"
          className="block max-w-xs truncate text-xs text-pine hover:underline"
        >
          {r.url}
        </a>
        {!r.error && (
          <span className="mt-1 block text-xs text-ink-3">
            {t('admin.webVitals.fieldLine', {
              scope: t(`admin.webVitals.scopes.${r.field.scope ?? 'none'}`),
            })}
            <br />
            {t('admin.webVitals.labLine', {
              lcp: sec(r.lab.lcpMs) ?? '—',
              tbt: r.lab.tbtMs ?? '—',
              cls: cls(r.lab.clsMilli) ?? '—',
            })}
          </span>
        )}
      </td>
      <td className="px-3 py-2">{t(`admin.webVitals.strategies.${r.strategy}`)}</td>
      {r.error ? (
        <td className="px-3 py-2 text-rose" colSpan={4}>
          {errorText(t, r.error)}
        </td>
      ) : (
        <>
          <td className="px-3 py-2 text-end">
            {r.lab.score === null ? (
              '—'
            ) : (
              <span className="inline-flex flex-col items-end gap-0.5">
                <Badge tone={TONE[scoreRating(r.lab.score)]}>{r.lab.score}</Badge>
                {diff !== null && diff !== 0 && (
                  <span className={`text-xs ${diff > 0 ? 'text-moss' : 'text-rose'}`}>
                    {t('admin.webVitals.change', { n: diff > 0 ? `+${diff}` : String(diff) })}
                  </span>
                )}
              </span>
            )}
          </td>
          <td className="px-3 py-2 text-end">
            <Metric
              value={r.field.lcpMs === null ? null : t('admin.webVitals.units.s', { n: sec(r.field.lcpMs)! })}
              rating={r.field.ratings.lcp}
            />
          </td>
          <td className="px-3 py-2 text-end">
            <Metric
              value={r.field.inpMs === null ? null : t('admin.webVitals.units.ms', { n: r.field.inpMs })}
              rating={r.field.ratings.inp}
            />
          </td>
          <td className="px-3 py-2 text-end">
            <Metric value={cls(r.field.clsMilli)} rating={r.field.ratings.cls} />
          </td>
        </>
      )}
    </tr>
  );
}

function RunsSection({ d }: { d: WebVitalsOverview }) {
  const t = useT();
  const { formatDate } = useI18n();
  const tone = { queued: 'slate', running: 'amber', completed: 'moss', failed: 'rose' } as const;
  return (
    <Section title={t('admin.webVitals.runsTitle')}>
      <ul
        className="divide-y divide-line rounded-md border border-line text-sm"
        aria-label={t('admin.webVitals.runsTitle')}
      >
        {d.runs.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <Badge tone={tone[r.status]}>{t(`admin.webVitals.runStatus.${r.status}`)}</Badge>
            <span>{formatDate(r.createdAt, true)}</span>
            <span className="text-ink-3">{t(`admin.webVitals.triggers.${r.trigger}`)}</span>
            {r.error && <span className="text-rose">{errorText(t, r.error)}</span>}
          </li>
        ))}
      </ul>
    </Section>
  );
}
